import React from 'react';
import { Node } from 'reactflow';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ChevronUp, Bot, Brain, Wrench, Code, Database } from 'lucide-react';
import { NodeNavigationService } from '@/services/node-navigation.service';
import { cn } from '@/lib/utils';
import { workflowColors } from '@/lib/workflow-colors';

interface NavigationButtonProps {
  direction: 'upstream' | 'downstream';
  targetNode: Node | null;
  onClick: () => void;
  className?: string;
}

export function NavigationButton({
  direction,
  targetNode,
  onClick,
  className,
}: NavigationButtonProps) {
  const metadata = NodeNavigationService.getNodeMetadata(targetNode);
  const isDisabled = !targetNode;

  const Icon = direction === 'upstream' ? ChevronLeft : ChevronRight;

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 transition-all',
        'hover:shadow-md hover:scale-105',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        direction === 'upstream' ? 'flex-row' : 'flex-row-reverse',
        className
      )}
    >
      <Icon className="h-4 w-4" />
      {metadata && (
        <div className="flex items-center gap-2">
          {metadata.icon && (
            <span className="text-base">{React.createElement(metadata.icon, { size: 16 })}</span>
          )}
          <span className="text-sm font-medium truncate max-w-[120px]">
            {metadata.label}
          </span>
        </div>
      )}
      {!metadata && (
        <span className="text-sm text-muted-foreground">
          {direction === 'upstream' ? 'No input' : 'No output'}
        </span>
      )}
    </Button>
  );
}

interface FloatingNavigationButtonProps extends NavigationButtonProps {
  position: 'left' | 'right';
}

export function FloatingNavigationButton({
  direction,
  targetNode,
  onClick,
  position,
}: FloatingNavigationButtonProps) {
  const metadata = NodeNavigationService.getNodeMetadata(targetNode);
  const isDisabled = !targetNode;

  if (isDisabled) {
    return null; // Don't show button if no target
  }

  const Icon = direction === 'upstream' ? ChevronLeft : ChevronRight;

  return (
    <div
      className={cn(
        'absolute top-1/2 -translate-y-1/2 z-10',
        position === 'left' ? '-left-3' : '-right-3'
      )}
    >
      <Button
        variant="secondary"
        size="icon"
        onClick={onClick}
        className={cn(
          'h-12 w-12 rounded-full shadow-lg',
          'hover:shadow-xl hover:scale-110',
          'transition-all duration-200',
          'bg-background border-2 border-border'
        )}
        title={metadata?.label || 'Navigate'}
      >
        <Icon className="h-5 w-5" />
      </Button>
    </div>
  );
}

// Wrapper component for both upstream and downstream navigation
interface NavigationButtonsProps {
  currentNodeId: string;
  nodes: Node[];
  edges: any[]; // Use any[] to avoid type conflicts with reactflow Edge type
  onNavigate: (nodeId: string) => void;
  className?: string;
  floating?: boolean; // New prop for floating mode
}

export function NavigationButtons({
  currentNodeId,
  nodes,
  edges,
  onNavigate,
  className,
  floating = false,
}: NavigationButtonsProps) {
  // Check if current node is a sub-node - if so, don't show navigation buttons
  const isSubNode = NodeNavigationService.isSubNode(currentNodeId, edges);
  
  if (isSubNode) {
    return null;
  }

  const upstreamNode = NodeNavigationService.getUpstreamNode(
    currentNodeId,
    nodes,
    edges
  );
  const downstreamNode = NodeNavigationService.getDownstreamNode(
    currentNodeId,
    nodes,
    edges
  );

  const upstreamMetadata = NodeNavigationService.getNodeMetadata(upstreamNode);
  const downstreamMetadata = NodeNavigationService.getNodeMetadata(downstreamNode);

  // Floating mode - position buttons on edges of panels
  if (floating) {
    // Get all downstream nodes grouped by handle
    const downstreamByHandle = NodeNavigationService.getDownstreamNodesByHandle(
      currentNodeId,
      nodes,
      edges
    );

    // Helper to truncate labels
    const truncateLabel = (label: string, maxLen = 12) =>
      label.length > maxLen ? label.substring(0, maxLen - 2) + '...' : label;

    return (
      <>
        {/* Previous Node Button - Left edge of Input Panel */}
        {upstreamNode && upstreamMetadata?.icon && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-2 z-20 flex flex-col items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onNavigate(upstreamNode.id)}
              className={cn(
                'h-12 w-12 rounded-full shadow-lg',
                'hover:shadow-xl hover:scale-110',
                'transition-all duration-200',
                'bg-background group relative'
              )}
            >
              {React.createElement(upstreamMetadata.icon, {
                className: "h-6 w-6",
                style: { color: upstreamMetadata.color }
              })}
              {/* Tooltip on hover */}
              <div className="absolute left-full ml-2 px-3 py-1.5 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border z-50">
                {upstreamMetadata.label}
              </div>
            </Button>
            {/* Label below button */}
            <span
              className="text-[10px] font-medium max-w-[60px] truncate text-center text-muted-foreground"
              title={upstreamMetadata.label}
            >
              {truncateLabel(upstreamMetadata.label)}
            </span>
          </div>
        )}

        {/* Next Node Buttons - Right edge of Output Panel */}
        {/* Show multiple buttons if there are multiple output routes */}
        {downstreamByHandle.length > 0 && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-2 z-20 flex flex-col gap-3">
            {downstreamByHandle.map(({ handle, handleLabel, node }) => {
              const metadata = NodeNavigationService.getNodeMetadata(node);
              if (!metadata?.icon) return null;

              return (
                <div key={`nav-${node.id}-${handle}`} className="flex flex-col items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onNavigate(node.id)}
                    className={cn(
                      'h-12 w-12 rounded-full shadow-lg',
                      'hover:shadow-xl hover:scale-110',
                      'transition-all duration-200',
                      'bg-background group relative'
                    )}
                  >
                    {React.createElement(metadata.icon, {
                      className: "h-6 w-6",
                      style: { color: metadata.color }
                    })}
                    {/* Tooltip on hover with handle label */}
                    <div className="absolute right-full mr-2 px-3 py-1.5 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border z-50">
                      <div className="font-semibold">{metadata.label}</div>
                      {downstreamByHandle.length > 1 && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          via {handleLabel}
                        </div>
                      )}
                    </div>
                  </Button>
                  {/* Label below button */}
                  <span
                    className="text-[10px] font-medium max-w-[60px] truncate text-center text-muted-foreground"
                    title={metadata.label}
                  >
                    {truncateLabel(metadata.label)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  // Original inline mode
  return (
    <div className={cn('flex items-center gap-2 p-4', className)}>
      <NavigationButton
        direction="upstream"
        targetNode={upstreamNode}
        onClick={() => upstreamNode && onNavigate(upstreamNode.id)}
      />
      <NavigationButton
        direction="downstream"
        targetNode={downstreamNode}
        onClick={() => downstreamNode && onNavigate(downstreamNode.id)}
      />
    </div>
  );
}

// Sub-node handle type configuration
const SUB_NODE_HANDLE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  chatModel: { label: 'Model', icon: Bot, color: workflowColors.handle.chatModel },
  memory: { label: 'Memory', icon: Database, color: workflowColors.handle.memory },
  tools: { label: 'Tools', icon: Wrench, color: workflowColors.handle.tools },
  parser: { label: 'Parser', icon: Code, color: workflowColors.handle.parser },
};

// Order for displaying sub-node buttons
const SUB_NODE_HANDLE_ORDER = ['chatModel', 'memory', 'tools', 'parser'];

interface SubNodeNavigationButtonsProps {
  currentNodeId: string;
  nodes: Node[];
  edges: any[];
  onNavigate: (nodeId: string) => void;
}

/**
 * Navigation buttons for AI Agent sub-nodes
 * - On sub-nodes: Shows a button at the top to navigate back to parent AI Agent
 * - On AI Agent: Shows buttons at the bottom for each connected sub-node
 */
export function SubNodeNavigationButtons({
  currentNodeId,
  nodes,
  edges,
  onNavigate,
}: SubNodeNavigationButtonsProps) {
  const isSubNode = NodeNavigationService.isSubNode(currentNodeId, edges);
  const currentNode = nodes.find(n => n.id === currentNodeId);

  // For sub-nodes: Show button to navigate back to parent AI Agent
  if (isSubNode) {
    const parentInfo = NodeNavigationService.getParentAgentNode(currentNodeId, nodes, edges);
    if (!parentInfo) return null;

    const parentMetadata = NodeNavigationService.getNodeMetadata(parentInfo.node);
    const handleConfig = SUB_NODE_HANDLE_CONFIG[parentInfo.handleType];

    return (
      <div className="absolute top-0 left-1/2 -translate-x-1/2 translate-y-2 z-20">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate(parentInfo.node.id)}
          className={cn(
            'h-10 px-4 rounded-full shadow-lg',
            'hover:shadow-xl hover:scale-105',
            'transition-all duration-200',
            'bg-background group relative flex items-center gap-2'
          )}
        >
          <ChevronUp className="h-4 w-4" />
          {parentMetadata?.icon && React.createElement(parentMetadata.icon, {
            className: "h-5 w-5",
            style: { color: parentMetadata.color }
          })}
          <span className="text-sm font-medium">{parentMetadata?.label || 'AI Agent'}</span>
          {/* Tooltip showing which anchor type this sub-node is */}
          {handleConfig && (
            <div className="absolute top-full mt-2 px-3 py-1.5 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border z-50">
              <span className="text-xs text-muted-foreground">Connected as </span>
              <span style={{ color: handleConfig.color }}>{handleConfig.label}</span>
            </div>
          )}
        </Button>
      </div>
    );
  }

  // For AI Agent nodes: Show buttons at the bottom for each connected sub-node
  const isAIAgentNode = currentNode?.type === 'ai-agent';
  if (!isAIAgentNode) return null;

  const connectedSubNodes = NodeNavigationService.getConnectedSubNodes(currentNodeId, nodes, edges);
  if (connectedSubNodes.length === 0) return null;

  // Sort sub-nodes by handle order
  const sortedSubNodes = [...connectedSubNodes].sort((a, b) => {
    const aIndex = SUB_NODE_HANDLE_ORDER.indexOf(a.handleType);
    const bIndex = SUB_NODE_HANDLE_ORDER.indexOf(b.handleType);
    return aIndex - bIndex;
  });

  return (
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20 flex gap-3">
      {sortedSubNodes.map(({ handleType, node }) => {
        const handleConfig = SUB_NODE_HANDLE_CONFIG[handleType];
        const nodeMetadata = NodeNavigationService.getNodeMetadata(node);
        if (!handleConfig) return null;

        const IconComponent = handleConfig.icon;
        const nodeLabel = nodeMetadata?.label || handleConfig.label;
        // Truncate label for display (max 12 chars)
        const truncatedLabel = nodeLabel.length > 12 ? nodeLabel.substring(0, 10) + '...' : nodeLabel;

        return (
          <div key={`subnode-nav-${node.id}`} className="flex flex-col items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onNavigate(node.id)}
              className={cn(
                'h-12 w-12 rounded-full shadow-lg',
                'hover:shadow-xl hover:scale-110',
                'transition-all duration-200',
                'bg-background group relative'
              )}
              style={{ borderColor: handleConfig.color }}
            >
              <IconComponent
                className="h-5 w-5"
                style={{ color: handleConfig.color }}
              />
              {/* Tooltip on hover - positioned above since buttons are at bottom */}
              <div className="absolute bottom-full mb-2 px-3 py-1.5 bg-popover text-popover-foreground text-sm rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap border z-50">
                <div className="font-semibold">{nodeLabel}</div>
                <div className="text-xs text-muted-foreground mt-0.5" style={{ color: handleConfig.color }}>
                  {handleConfig.label}
                </div>
              </div>
            </Button>
            {/* Label below button - always show like left/right buttons */}
            <span
              className="text-[10px] font-medium max-w-[60px] truncate text-center text-muted-foreground"
              title={nodeLabel}
            >
              {truncatedLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
}
